import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import type { SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import type { FirestoreAssignmentRubric } from '@breakoutlearning/firebase-repository/models/AssignmentRubric'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { useEffect, useMemo, useState } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import { ConfigureScoreMapping } from './ConfigureScoreMapping'
import { ConfigureWeightsDialog } from './ConfigureWeightsDialog'
import {
  ALL_ROLES_COLUMN,
  SCALES,
  applicableRoleIdsForRubric,
  columnSum,
  equalSplit,
  isRubricEligibleForCustomGrading,
  isScoreMappingDescending,
  isWeightGridValid,
  rubricAppliesToColumn,
  scaleForRubricType,
  type CustomRubricsConfig,
  type RubricConfigPayload,
  type ScaleId,
  type WeightColumn,
  type WeightGrid,
} from './customRubricsConfig'

export function ConfigureCustomRubrics({
  rubrics,
  roles,
  existingConfig,
  disabled,
  configRef,
  onValidityChange,
  onDirtyChange,
  weightsDialogOpen = false,
  onWeightsDialogClose,
}: {
  rubrics: SlideRubric[]
  roles: SlideDeckRole[]
  existingConfig?: Map<string, FirestoreAssignmentRubric>
  disabled?: boolean
  configRef: React.MutableRefObject<CustomRubricsConfig>
  onValidityChange?: (isValid: boolean) => void
  /** Fires when the weights/score-mapping have diverged from the initial
   * config — i.e. there are unsaved custom-rubric changes. The weights dialog's
   * own Save only commits to in-memory state, so the parent uses this to warn
   * before the grading dialog is closed. */
  onDirtyChange?: (isDirty: boolean) => void
  /** Controlled open state for the weights dialog (triggered from the grading
   * column's "Customize weights" link). */
  weightsDialogOpen?: boolean
  onWeightsDialogClose?: () => void
}) {
  const { tt } = useTranslationTyped()

  const eligibleRubrics = useMemo(
    () => rubrics.filter(isRubricEligibleForCustomGrading),
    [rubrics]
  )

  const scalesPresent = useMemo(
    () =>
      SCALES.filter((scale) =>
        eligibleRubrics.some((r) =>
          scale.rubricTypes.includes(r.data.rubricType)
        )
      ),
    [eligibleRubrics]
  )

  const columns: WeightColumn[] = useMemo(() => {
    if (roles.length === 0) {
      return [
        {
          key: ALL_ROLES_COLUMN,
          label: tt.instructor_library.custom_rubrics_all_roles(),
        },
      ]
    }
    return roles.map((role) => ({ key: role.id, label: role.data.roleName }))
  }, [roles, tt])

  // Seed each scale's mapping from existing config (a rubric of that scale) or
  // the scale's default mapping.
  const defaultMappings: Record<
    ScaleId,
    Record<string, number>
  > = useMemo(() => {
    const result = {} as Record<ScaleId, Record<string, number>>
    for (const scale of SCALES) {
      let mapping: Record<string, number> | undefined
      if (existingConfig) {
        const rubricOfScale = eligibleRubrics.find((r) =>
          scale.rubricTypes.includes(r.data.rubricType)
        )
        if (rubricOfScale) {
          mapping = existingConfig.get(rubricOfScale.id)?.scoreMapping
        }
      }
      result[scale.id] = mapping ?? scale.defaultMapping
    }
    return result
  }, [existingConfig, eligibleRubrics])

  // Seed the weight grid per rubric from existing config, or an equal split of
  // 100% across all rubrics, per column.
  const defaultGrid: WeightGrid = useMemo(() => {
    const grid: WeightGrid = {}
    for (const rubric of eligibleRubrics) grid[rubric.id] = {}
    if (existingConfig) {
      for (const rubric of eligibleRubrics) {
        const weights = existingConfig.get(rubric.id)?.weights
        for (const col of columns) {
          if (!weights) grid[rubric.id][col.key] = 0
          else if ('allRoles' in weights)
            grid[rubric.id][col.key] = Math.round(weights.allRoles)
          else
            grid[rubric.id][col.key] = Math.round(weights.byRole[col.key] ?? 0)
        }
      }
      return grid
    }
    // No existing config: per column, split 100% across the rubrics applicable
    // to that column (role); non-applicable rubrics stay at 0.
    for (const col of columns) {
      const applicable = eligibleRubrics.filter((r) =>
        rubricAppliesToColumn(r, col.key, roles)
      )
      const split = equalSplit(100, applicable.length)
      let i = 0
      for (const rubric of eligibleRubrics) {
        grid[rubric.id][col.key] = rubricAppliesToColumn(rubric, col.key, roles)
          ? split[i++]
          : 0
      }
    }
    return grid
  }, [existingConfig, eligibleRubrics, columns, roles])

  const [activeScale, setActiveScale] = useState<ScaleId | undefined>(
    scalesPresent[0]?.id
  )
  const [mappings, setMappings] =
    useState<Record<ScaleId, Record<string, number>>>(defaultMappings)
  const [grid, setGrid] = useState<WeightGrid>(defaultGrid)

  // Build the payload + validity and publish to the shared ref + parent.
  useEffect(() => {
    const mappingsValid = scalesPresent.every((scale) =>
      isScoreMappingDescending(mappings[scale.id], scale.levels)
    )
    const gridValid = isWeightGridValid(grid, columns, eligibleRubrics, roles)
    const isValid = mappingsValid && gridValid

    const rubricConfigs: RubricConfigPayload[] = eligibleRubrics.map(
      (rubric) => {
        const scale = scaleForRubricType(rubric.data.rubricType)
        const rubricGrid = grid[rubric.id] ?? {}

        const weights =
          roles.length === 0
            ? { allRoles: rubricGrid[ALL_ROLES_COLUMN] ?? 0 }
            : {
                // Only the roles this rubric applies to become byRole keys —
                // key presence is role applicability downstream (TS scoring and
                // Go evaluation), so a role-specific rubric must not list roles
                // it doesn't apply to.
                byRole: Object.fromEntries(
                  applicableRoleIdsForRubric(rubric, roles).map((id) => [
                    id,
                    rubricGrid[id] ?? 0,
                  ])
                ),
              }

        return {
          rubricId: rubric.id,
          weights,
          scoreMapping: scale ? mappings[scale.id] : {},
        }
      }
    )

    // The panel is always shown when rendered, so custom grading is always on.
    configRef.current = { enabled: true, isValid, rubricConfigs }
    onValidityChange?.(isValid)

    // Dirty = weights or score mappings differ from the initial (saved) config.
    const dirty =
      JSON.stringify(grid) !== JSON.stringify(defaultGrid) ||
      JSON.stringify(mappings) !== JSON.stringify(defaultMappings)
    onDirtyChange?.(dirty)
  }, [
    mappings,
    grid,
    columns,
    scalesPresent,
    eligibleRubrics,
    roles,
    configRef,
    onValidityChange,
    onDirtyChange,
    defaultGrid,
    defaultMappings,
  ])

  const activeScaleDef = SCALES.find((s) => s.id === activeScale)

  if (scalesPresent.length === 0 || !activeScaleDef) return null

  const weightsInvalid = !isWeightGridValid(
    grid,
    columns,
    eligibleRubrics,
    roles
  )
  const weightsSums = columns.map((col) =>
    columnSum(grid, col.key, eligibleRubrics, roles)
  )

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-row items-center gap-1">
        <h3 className="text-title-medium">
          {tt.instructor_library.custom_rubrics_score_mapping_title()}
        </h3>
        <BreakoutInfoTooltip
          content={tt.instructor_library.custom_rubrics_score_mapping_tooltip()}
        />
      </div>

      {scalesPresent.length > 1 && (
        <div className="flex w-fit flex-row gap-2 rounded-2xl border border-outline-light bg-white p-2">
          {scalesPresent.map((scale) => (
            <BreakoutButton
              key={scale.id}
              className="!min-w-0"
              size="medium"
              kind={scale.id === activeScale ? 'accent' : 'secondary'}
              onClick={() => setActiveScale(scale.id)}
              data-testid={`score-mapping-tab-${scale.id}`}
            >
              {tt.instructor_library[scale.labelKey]()}
            </BreakoutButton>
          ))}
        </div>
      )}

      <ConfigureScoreMapping
        levels={activeScaleDef.levels}
        rubricType={activeScaleDef.rubricTypes[0]}
        values={mappings[activeScale!]}
        disabled={disabled}
        onChange={(next) =>
          setMappings((prev) => ({ ...prev, [activeScale!]: next }))
        }
      />

      {weightsInvalid && (
        <div className="text-label-medium text-core-error">
          {tt.instructor_library.custom_rubrics_weights_sum_error()} (
          {weightsSums.map((s) => `${s}%`).join(', ')})
        </div>
      )}

      {weightsDialogOpen && (
        <ConfigureWeightsDialog
          rubrics={eligibleRubrics}
          roles={roles}
          initialGrid={grid}
          disabled={disabled}
          onSave={(nextGrid) => {
            setGrid(nextGrid)
            onWeightsDialogClose?.()
          }}
          onClose={() => onWeightsDialogClose?.()}
        />
      )}
    </div>
  )
}
